// ==================== SUB-ACCOUNT ITEM COMPONENT ====================

import {
  Mail,
  Clock,
  Pencil,
  KeyRound,
  Eye,
  EyeOff,
  Trash2,
  MoreVertical,
  UserCheck,
  UserX,
  Users,
  Building2,
} from 'lucide-react';
import { Badge, Dropdown } from '../ui';
import type { SubAccount } from '../../services/sub-accounts.service';

// ==================== STATUS BADGE COMPONENT ====================

export const SubAccountStatusBadge = ({ isActive }: { isActive: boolean }) => {
  const config = isActive
    ? { variant: 'success' as const, label: 'Active', icon: UserCheck }
    : { variant: 'warning' as const, label: 'Inactive', icon: UserX };

  const { variant, label, icon: Icon } = config;

  return (
    <Badge variant={variant} className="flex items-center gap-1 text-xs">
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
};

// ==================== SUB-ACCOUNT ITEM COMPONENT ====================

interface SubAccountItemProps {
  subAccount: SubAccount;
  accountName?: string;
  onEdit: () => void;
  onSetPassword: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
  /** Opens the assigned contacts (only the customer assigns contacts) */
  onManageContacts?: () => void;
  /** 'row' for list cards on a page, 'card' for lists inside a modal */
  variant?: 'row' | 'card';
}

export const SubAccountItem = ({
  subAccount,
  accountName,
  onEdit,
  onSetPassword,
  onToggleStatus,
  onDelete,
  onManageContacts,
  variant = 'row',
}: SubAccountItemProps) => {
  const contactCount = subAccount.contactCount || 0;

  const dropdownItems = [
    ...(onManageContacts
      ? [
          {
            key: 'manage-contacts',
            label: 'Manage Contacts',
            icon: <Users className="w-4 h-4" />,
            onClick: onManageContacts,
          },
        ]
      : []),
    {
      key: 'edit-sub-account',
      label: 'Edit Name',
      icon: <Pencil className="w-4 h-4" />,
      onClick: onEdit,
    },
    {
      key: 'set-password',
      label: 'Set New Password',
      icon: <KeyRound className="w-4 h-4" />,
      onClick: onSetPassword,
    },
    {
      key: 'toggle-status',
      label: subAccount.isActive ? 'Deactivate' : 'Activate',
      icon: subAccount.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />,
      onClick: onToggleStatus,
    },
    {
      key: 'delete-sub-account',
      label: 'Delete Sub-account',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: onDelete,
      colorClass: 'text-red-400 hover:text-red-300',
    },
  ];

  const initials = `${subAccount.firstName.charAt(0)}${subAccount.lastName.charAt(0)}`.toUpperCase();

  return (
    <div
      className={
        variant === 'card'
          ? 'flex items-center gap-4 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-all'
          : 'flex items-center gap-4 p-4 transition-colors hover:bg-white/5'
      }
    >
      {/* Avatar */}
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500/20 to-amber-500/20 flex items-center justify-center flex-shrink-0">
        <span className="text-sm font-medium text-orange-400">{initials}</span>
      </div>

      {/* Sub-account Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="text-sm font-medium text-white truncate">
            {subAccount.firstName} {subAccount.lastName}
          </h4>
          <SubAccountStatusBadge isActive={subAccount.isActive} />
        </div>

        <div className="flex items-center gap-4 mt-2 flex-wrap">
          {accountName && (
            <span className="text-xs text-(--color-muted-gray) flex items-center gap-1 truncate">
              <Building2 className="w-3 h-3" />
              {accountName}
            </span>
          )}
          <span className="text-xs text-(--color-muted-gray) flex items-center gap-1 truncate">
            <Mail className="w-3 h-3" />
            {subAccount.email}
          </span>
          <span className="text-xs text-(--color-muted-gray) flex items-center gap-1">
            <Users className="w-3 h-3" />
            {contactCount} contact{contactCount !== 1 ? 's' : ''}
          </span>
          <span className="text-xs text-(--color-muted-gray) flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {subAccount.lastLoginAt
              ? `Last sign-in ${new Date(subAccount.lastLoginAt).toLocaleDateString()}`
              : 'Never signed in'}
          </span>
          <span className="text-xs text-(--color-muted-gray)">
            Created {new Date(subAccount.createdAt).toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Actions Dropdown */}
      <div className="shrink-0">
        <Dropdown
          trigger={<MoreVertical className="w-4 h-4" />}
          items={dropdownItems}
          align="right"
        />
      </div>
    </div>
  );
};

export default SubAccountItem;
